import { queryOptions, useQuery } from '@tanstack/react-query'
import { api, ApiError, unwrap } from '~/lib/api'
import { sessionQuery, type Session } from '~/lib/session'

export type PublicConfig = { appName: string; registrationOpen: boolean }

const CONFIG_DEFAULTS: PublicConfig = { appName: 'SquadCore', registrationOpen: true }

// GET /api/config (modules/org/public.ts) is optional for these pages: until it is mounted the path
// falls through to the session check (401) or the router (404). Any failure means "defaults", and
// plain fetch keeps a 401 here from triggering the global logout handler.
export const configQuery = queryOptions({
  queryKey: ['config'],
  queryFn: async (): Promise<PublicConfig> => {
    try {
      const res = await fetch('/api/config', { credentials: 'same-origin' })
      if (!res.ok) return CONFIG_DEFAULTS
      return { ...CONFIG_DEFAULTS, ...((await res.json()) as Partial<PublicConfig>) }
    } catch {
      return CONFIG_DEFAULTS
    }
  },
  staleTime: 5 * 60_000,
})

export function useRegistrationOpen() {
  return useQuery(configQuery).data?.registrationOpen ?? true
}

/** The session if logged in, null if not (no error, no redirect). */
export async function optionalSession(fetch: (q: typeof sessionQuery) => Promise<Session>) {
  try {
    return await fetch(sessionQuery)
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null
    throw e
  }
}

export const invitationQuery = (token: string) =>
  queryOptions({
    queryKey: ['auth', 'invitation', token],
    queryFn: () => unwrap(api.auth.invitations[':token'].$get({ param: { token } })),
    retry: false,
    staleTime: 60_000,
  })

export type Invitation = Awaited<ReturnType<NonNullable<ReturnType<typeof invitationQuery>['queryFn']>>>

export const emailChangeQuery = (token: string) =>
  queryOptions({
    queryKey: ['auth', 'email-change', token],
    queryFn: () => unwrap(api.auth.email.confirm.$get({ query: { token } })),
    retry: false,
    staleTime: 60_000,
  })
